/**
 * Design System - ListItem Component
 * Reusable list item for selectable lists
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  dimensions,
  strokeColors,
} from '../../utils';

export interface ListItemProps {
  label: string;
  onClick?: () => void;
  isSelected?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const ListItem: React.FC<ListItemProps> = ({
  label,
  onClick,
  isSelected = false,
  size = 'md',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const [needsTooltip, setNeedsTooltip] = useState(false);

  const sizeConfig = {
    sm: {
      height: '32px',
      paddingX: spacing[3],
      fontSize: typography.size.xs,
    },
    md: {
      height: dimensions.component.suggestedItem.height,
      paddingX: spacing[4],
      fontSize: typography.size.sm,
    },
    lg: {
      height: '48px',
      paddingX: spacing[5],
      fontSize: typography.size.base,
    },
  };

  const config = sizeConfig[size];

  // Check if text is actually truncated
  useEffect(() => {
    if (textRef.current) {
      const isTruncated =
        textRef.current.scrollWidth > textRef.current.clientWidth;
      setNeedsTooltip(isTruncated);
    }
  }, [label]);

  const buttonContent = (
    <button
      onClick={onClick}
      tabIndex={-1}
      style={{
        width: '100%',
        minHeight: config.height,
        backgroundColor: isSelected
          ? backgroundColors.shade
          : isHovered && onClick
            ? backgroundColors.secondary
            : backgroundColors.primary,
        borderColor: 'transparent',
        borderWidth: borders.width.none,
        paddingLeft: config.paddingX,
        paddingRight: config.paddingX,
        paddingTop: spacing[2],
        paddingBottom: spacing[2],
        display: 'flex',
        alignItems: 'flex-start',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        fontFamily: fontPrimary,
        fontSize: config.fontSize,
        fontWeight: typography.weight.regular,
        color: isSelected ? typographyColors.primary : typographyColors.primary,
        textAlign: 'left',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
      }}
      onMouseEnter={e => {
        setIsHovered(true);
        if (needsTooltip) {
          setIsPopoverOpen(true);
        }
        if (onClick && !isSelected) {
          e.currentTarget.style.backgroundColor = backgroundColors.secondary;
        }
      }}
      onMouseLeave={e => {
        setIsHovered(false);
        setIsPopoverOpen(false);
        if (onClick && !isSelected) {
          e.currentTarget.style.backgroundColor = backgroundColors.primary;
        }
      }}
    >
      <span
        ref={textRef}
        style={{
          width: '100%',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {label}
      </span>
    </button>
  );

  // If tooltip is needed, wrap in Popover
  if (needsTooltip) {
    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>{buttonContent}</PopoverTrigger>
        <PopoverContent
          style={{
            maxWidth: '400px',
            padding: spacing[3],
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            color: typographyColors.primary,
            lineHeight: typography.lineHeight.relaxed,
            backgroundColor: backgroundColors.primary,
            borderColor: strokeColors.base,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          side='top'
          align='start'
          sideOffset={8}
          onMouseEnter={() => setIsPopoverOpen(true)}
          onMouseLeave={() => setIsPopoverOpen(false)}
        >
          {label}
        </PopoverContent>
      </Popover>
    );
  }

  return buttonContent;
};
